import React, { useMemo, useState } from "react";
import { api, extractError } from "../../lib/api";
import { SectionCard, StatePill } from "../Ui";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FormActions, FormSection } from "../forms/FormField";
import { DEFAULT_PADDY_LAB_VALUES, PaddyLabAnalysisFields } from "../forms/PaddyLabAnalysisFields";
import { PartySelect } from "../forms/PartySelect";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FormField } from "../forms/FormField";
import { Input } from "../ui/input";
import LabTabShell from "./LabTabShell";
import PaddyLabReportRegister from "./PaddyLabReportRegister";
import SampleNotReceivedDialog from "./SampleNotReceivedDialog";
import { mapNumberFields, PADDY_LAB_NUMBER_KEYS, parseNumberInput } from "../../lib/numberInput";
import { isAdmin, useAuth } from "../../context/AuthContext";
import BackendExportButton from "../BackendExportButton";
import { PADDY_LAB_METRICS } from "../../lib/paddyLabConfig";

export function CompositeLabTab() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const { data, reload } = usePageBootstrap({
    path: "/lab/bootstrap",
    params: { tab: "composite" },
    wsTypes: ["lab", "purchase"],
  });
  useBootstrapWS(["lab", "purchase"], reload);
  const parties = data?.parties || [];
  const queue = data?.queue || [];
  const correctionQueue = data?.correction_queue || [];
  const reports = data?.reports || [];
  const correctionCount = data?.correction_count ?? correctionQueue.length;
  const [partyId, setPartyId] = useState("");
  const [purchaseId, setPurchaseId] = useState("");
  const [bagsSampled, setBagsSampled] = useState("");
  const [form, setForm] = useState({ ...DEFAULT_PADDY_LAB_VALUES });
  const [submitting, setSubmitting] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [waivingId, setWaivingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const weightSummary = useMemo(
    () => (admin ? resolveCompositeWeightSummary(data?.weight_summary, reports) : null),
    [admin, data?.weight_summary, reports],
  );

  const selectable = useMemo(() => {
    const byId = new Map();
    for (const p of [...correctionQueue, ...queue]) {
      byId.set(String(p.id), p);
    }
    return Array.from(byId.values());
  }, [queue, correctionQueue]);

  const filtered = useMemo(() => {
    if (!partyId) return selectable;
    const pid = String(partyId);
    return selectable.filter(
      (p) =>
        String(p.party_id) === pid
        || parties.some(
          (party) => String(party.id) === pid && party.display_name === p.party_name,
        ),
    );
  }, [selectable, partyId, parties]);

  const selectedPurchase = selectable.find((p) => String(p.id) === String(purchaseId))
    || filtered.find((p) => String(p.id) === String(purchaseId));
  const selectedReport = reports.find((r) => String(r.purchase_id) === String(purchaseId));
  const canMarkSampleNotReceived = Boolean(
    selectedPurchase
    && (
      queue.some((p) => String(p.id) === String(selectedPurchase.id))
      || correctionQueue.some((p) => String(p.id) === String(selectedPurchase.id))
    ),
  );

  const onPartyChange = (id) => {
    setPartyId(id);
    setPurchaseId("");
    setCorrecting(false);
    setBagsSampled("");
    setForm({ ...DEFAULT_PADDY_LAB_VALUES });
  };

  const reportToForm = (report) => {
    if (!report) return { ...DEFAULT_PADDY_LAB_VALUES };
    return {
      ...DEFAULT_PADDY_LAB_VALUES,
      moisture_pct: report.moisture_pct ?? "",
      yield_pct: report.yield_pct ?? "",
      broken_pct: report.broken_pct ?? "",
      damage_pct: report.damage_pct ?? "",
      green_pct: report.green_pct ?? "",
      immature_pct: report.immature_pct ?? "",
      admixture_pct: report.admixture_pct ?? "",
      pr_pct: report.pr_pct ?? "",
      length_mm: report.length_mm ?? "",
      choba_pct: report.choba_pct ?? "",
      kachri_pct: report.kachri_pct ?? "",
      custom_param_1_name: report.custom_param_1_name || "",
      custom_param_1_value: report.custom_param_1_value || "",
      custom_param_2_name: report.custom_param_2_name || "",
      custom_param_2_value: report.custom_param_2_value || "",
      notes: report.notes || "",
    };
  };

  const selectCorrection = (p) => {
    if (p?.party_id) setPartyId(String(p.party_id));
    setPurchaseId(String(p.id));
    setCorrecting(true);
    const existing = reports.find((r) => String(r.purchase_id) === String(p.id));
    setForm(reportToForm(existing));
    setBagsSampled(existing?.bags_sampled != null ? String(existing.bags_sampled) : "");
  };

  const requestSampleNotReceived = (purchase) => {
    const target = purchase || selectedPurchase;
    if (!target?.id) return toast.error("Select a purchase");
    setConfirmTarget(target);
  };

  const markSampleNotReceived = async () => {
    const target = confirmTarget;
    if (!target?.id) return;
    setWaivingId(target.id);
    try {
      await api.post("/composite-lab-reports/sample-not-received", { purchase_id: +target.id });
      toast.success("Marked sample not received — no composite lab report");
      setConfirmTarget(null);
      if (String(purchaseId) === String(target.id)) {
        setPurchaseId("");
        setCorrecting(false);
        setBagsSampled("");
        setForm({ ...DEFAULT_PADDY_LAB_VALUES });
      }
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setWaivingId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!purchaseId) return toast.error("Select a purchase");
    setSubmitting(true);
    try {
      const saved = await api.post("/composite-lab-reports", {
        purchase_id: +purchaseId,
        bags_sampled: parseNumberInput(bagsSampled, { integer: true }),
        ...mapNumberFields(form, PADDY_LAB_NUMBER_KEYS),
      });
      if (saved?.validation_pending) {
        const labels = (saved.out_of_range || []).map(
          (key) => PADDY_LAB_METRICS.find((m) => m.key === key)?.label || key,
        );
        toast.warning(
          labels.length
            ? `Saved, but needs correction: ${labels.join(", ")}. Fix values and re-submit.`
            : "Saved, but values are outside allowed range — open Needs correction to fix.",
        );
      } else {
        toast.success(correcting ? "Corrected composite lab saved" : "Composite lab results recorded");
      }
      setPurchaseId("");
      setCorrecting(false);
      setBagsSampled("");
      setForm({ ...DEFAULT_PADDY_LAB_VALUES });
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const selectPurchase = (id) => {
    const p = selectable.find((x) => String(x.id) === String(id));
    if (p?.party_id) setPartyId(String(p.party_id));
    setPurchaseId(String(id));
    const fromCorrection = correctionQueue.some((x) => String(x.id) === String(id));
    setCorrecting(fromCorrection);
    if (fromCorrection) {
      const existing = reports.find((r) => String(r.purchase_id) === String(id));
      setForm(reportToForm(existing));
      setBagsSampled(existing?.bags_sampled != null ? String(existing.bags_sampled) : "");
    }
  };

  const summaryCard = admin && weightSummary ? (
    <SectionCard
      title="Avg yield"
      subtitle="Σ(yield % × vehicle net weight) ÷ Σ(vehicle net weight) · 0% yield labs excluded"
      testId="composite-yield-weight-summary"
      action={(
        <BackendExportButton
          path="/composite-lab-reports/weight-avg/export"
          filename={`composite-lab-weight-avg-${Date.now()}.xlsx`}
          label="Download full report"
          testId="composite-weight-avg-export"
        />
      )}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <SummaryStat label="Reports weighed" value={weightSummary.report_count ?? 0} />
        <SummaryStat
          label="Total vehicle weight"
          value={`${Number(weightSummary.total_weight_qtl || 0).toLocaleString("en-IN")} qtl`}
        />
        <SummaryStat
          label="Avg yield"
          value={`${Number(weightSummary.weighted_avg_yield_pct || 0).toLocaleString("en-IN")}%`}
          highlight
        />
        <SummaryStat
          label="Avg moisture"
          value={`${Number(weightSummary.weighted_avg_moisture_pct || 0).toLocaleString("en-IN")}%`}
        />
        <SummaryStat
          label="Avg broken"
          value={`${Number(weightSummary.weighted_avg_broken_pct || 0).toLocaleString("en-IN")}%`}
        />
      </div>
    </SectionCard>
  ) : null;

  const record = (
    <>
      {correctionQueue.length > 0 && (
        <SectionCard title={`Needs correction (${correctionQueue.length})`} testId="composite-lab-correction-queue">
          <p className="text-xs text-muted-foreground mb-3">
            These composite reports have values outside the allowed lab range. Fix readings, or mark sample not received.
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {correctionQueue.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2"
                data-testid={`composite-correction-${p.code}`}
              >
                <button
                  type="button"
                  onClick={() => selectCorrection(p)}
                  className="min-w-0 flex-1 text-left flex flex-wrap items-center gap-2 hover:opacity-80"
                >
                  {p.vehicle_number && (
                    <span className="text-xs font-medium uppercase">{p.vehicle_number}</span>
                  )}
                  <span className="font-mono text-xs">{p.code}</span>
                  <span className="text-sm font-medium">{p.party_name}</span>
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs shrink-0 bg-card"
                  disabled={waivingId === p.id}
                  onClick={() => requestSampleNotReceived(p)}
                  data-testid={`composite-correction-sample-not-received-${p.code}`}
                >
                  {waivingId === p.id ? "Saving…" : "Sample not received"}
                </Button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {queue.length > 0 && (
        <SectionCard title={`Awaiting composite sample (${queue.length})`} testId="composite-lab-queue">
          <p className="text-xs text-muted-foreground mb-3">
            Unloading and tare are done. Click a row to enter readings, or mark sample not received if no sample arrived.
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {queue.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-teal-200 bg-teal-50/40 px-3 py-2"
                data-testid={`composite-queue-${p.code}`}
              >
                <button
                  type="button"
                  onClick={() => selectPurchase(p.id)}
                  className="min-w-0 flex-1 text-left flex flex-wrap items-center gap-2 hover:opacity-80"
                >
                  {p.vehicle_number && (
                    <span className="text-xs font-medium uppercase">{p.vehicle_number}</span>
                  )}
                  <span className="font-mono text-xs">{p.code}</span>
                  <StatePill state={p.state} />
                  <span className="text-sm font-medium">{p.party_name}</span>
                  <span className="text-xs text-muted-foreground">{p.variety}</span>
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs shrink-0 bg-card"
                  disabled={waivingId === p.id}
                  onClick={() => requestSampleNotReceived(p)}
                  data-testid={`composite-sample-not-received-${p.code}`}
                >
                  {waivingId === p.id ? "Saving…" : "Sample not received"}
                </Button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Composite lab (post-unloading)">
        <p className="text-sm text-muted-foreground mb-4">
          Submit composite sample analysis after unloading is complete.
        </p>
        {correcting && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-950">
            Correcting flagged report
            {selectedReport?.out_of_range?.length
              ? `: ${(selectedReport.out_of_range || []).map(
                (key) => PADDY_LAB_METRICS.find((m) => m.key === key)?.label || key,
              ).join(", ")}`
              : ""}
            . Fix the highlighted parameters and submit again.
          </div>
        )}
        <form onSubmit={submit} data-testid="composite-lab-form">
          <FormSection title="Selection">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PartySelect
                parties={parties}
                value={partyId}
                onChange={onPartyChange}
                required
                disabled={!parties.length}
                testId="composite-lab-party"
              />
              <FormField
                label="Purchase (post-unloading)"
                required
                hint={`${filtered.length} purchase(s) available`}
                className="md:col-span-1"
              >
                <Select
                  value={purchaseId || "_none"}
                  onValueChange={(v) => selectPurchase(v === "_none" ? "" : v)}
                  disabled={!partyId}
                >
                  <SelectTrigger data-testid="composite-lab-purchase">
                    <SelectValue placeholder={!partyId ? "Select party first…" : "Select purchase…"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" disabled>Select purchase…</SelectItem>
                    {filtered.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.vehicle_number ? `${String(p.vehicle_number).toUpperCase()} · ` : ""}
                        {p.code} — {p.party_name} · {p.variety}
                        {correctionQueue.some((c) => String(c.id) === String(p.id)) ? " · Needs correction" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            {selectedPurchase && (
              <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                {selectedPurchase.vehicle_number && (
                  <span><strong>Vehicle:</strong> {String(selectedPurchase.vehicle_number).toUpperCase()}</span>
                )}
                <span><strong>Party:</strong> {selectedPurchase.party_name}</span>
              </div>
            )}
          </FormSection>

          <FormSection title="Paddy quality analysis" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <PaddyLabAnalysisFields values={form} onChange={setForm} testIdPrefix="comp-lab" />
              <FormField label="Bags sampled">
                <Input
                  type="number"
                  value={bagsSampled}
                  onChange={(e) => setBagsSampled(e.target.value)}
                  placeholder="Total bags sampled"
                  data-testid="comp-lab-bags-sampled"
                />
              </FormField>
            </div>
          </FormSection>

          <FormActions>
            <Button
              type="button"
              variant="outline"
              disabled={submitting || waivingId != null || !canMarkSampleNotReceived}
              onClick={() => requestSampleNotReceived(selectedPurchase)}
              data-testid="composite-lab-sample-not-received"
            >
              {waivingId && String(waivingId) === String(purchaseId) ? "Saving…" : "Sample not received"}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !purchaseId}
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white sm:min-w-[160px]"
              data-testid="composite-lab-submit"
            >
              {submitting ? "Submitting…" : correcting ? "Re-submit corrected report" : "Submit composite report"}
            </Button>
          </FormActions>
        </form>
      </SectionCard>
    </>
  );

  const register = (
    <PaddyLabReportRegister
      title={`Composite lab register (${reports.length})`}
      reports={reports}
      kind="composite"
      testId="composite-lab-register"
      onReload={reload}
    />
  );

  return (
    <div className="space-y-6">
      {summaryCard}
      <LabTabShell
        record={record}
        register={register}
        registerCount={reports.length}
        correctionCount={correctionCount}
        testId="composite-lab-tabs"
      />
      <SampleNotReceivedDialog
        open={Boolean(confirmTarget)}
        purchase={confirmTarget}
        kind="composite"
        confirming={waivingId != null}
        onCancel={() => !waivingId && setConfirmTarget(null)}
        onConfirm={markSampleNotReceived}
      />
    </div>
  );
}

/** Prefer server summary; fall back to Σ(yield × net weight) / Σ(net weight) from loaded reports. */
function resolveCompositeWeightSummary(serverSummary, reports = []) {
  if (serverSummary && Number(serverSummary.report_count || 0) > 0) {
    return serverSummary;
  }
  let totalWeight = 0;
  let yieldSum = 0;
  let moistureSum = 0;
  let brokenSum = 0;
  let admixtureSum = 0;
  let count = 0;
  for (const row of reports) {
    if (String(row.verdict || row.status_label || "").toUpperCase().includes("SAMPLE_NOT_RECEIVED")
      || row.status_label === "Sample not received") {
      continue;
    }
    const w = Number(row.net_weight_qtl || 0);
    const y = Number(row.yield_pct || 0);
    if (!(w > 0) || !(y > 0)) continue;
    count += 1;
    totalWeight += w;
    yieldSum += w * y;
    moistureSum += w * Number(row.moisture_pct || 0);
    brokenSum += w * Number(row.broken_pct || 0);
    admixtureSum += w * Number(row.admixture_pct || 0);
  }
  return {
    report_count: count,
    total_weight_qtl: Math.round(totalWeight * 1000) / 1000,
    weighted_avg_yield_pct: totalWeight ? Math.round((yieldSum / totalWeight) * 100) / 100 : 0,
    weighted_avg_moisture_pct: totalWeight ? Math.round((moistureSum / totalWeight) * 100) / 100 : 0,
    weighted_avg_broken_pct: totalWeight ? Math.round((brokenSum / totalWeight) * 100) / 100 : 0,
    weighted_avg_admixture_pct: totalWeight ? Math.round((admixtureSum / totalWeight) * 100) / 100 : 0,
  };
}

function SummaryStat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-serif text-2xl mt-1 tabular ${highlight ? "text-primary" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}
